/* eslint-disable no-unused-expressions */
import { MFA_ENDPOINT_FILE_PATH } from 'constants/AppConstants';
import load from './load';

export default (() => {
  /**
   * @name MicrofrontendUtils#instance
   * @description instance of Microfrontend.
   * @type Microfrontend
   */
  let instance;

  /**
   * Microfrontend Class - Utility class to handle load, mount/unmount and prefetch micro applications
   */
  class Microfrontend {
    constructor() {
      /**
       * @name Microfrontend#mountedList
       * @description List of currently mounted micro applications.
       * @type Array
       */
      this.mountedList = [];
      /**
       * @name Microfrontend#mountedList
       * @description List of currently loaded micro applications.
       * @type Array
       */
      this.loadedList = [];

      // Variable to keep track of currently mounting application
      this.mountingCurrentApp = '';
    }

    /**
     * setter for loadedList
     * @param apps - list of micro applications
     */
    setLoadedList = (apps) => {
      this.loadedList = apps;
    }

    /**
     * loads asset-manifest.json from provided URL
     * @param {String} url - Micro application endpoint
     * @returns {Promise}
     */
    getJson = (url) => fetch(`${url}/${MFA_ENDPOINT_FILE_PATH}`, { cache: 'no-store' });

    /**
     * mounts micro application and adds to mountedList
     * @param {{name: String, elementId: String, props: Object}} config - Micro application details
     * @returns status
     */
    mountMF = (config) => {
      const { name, elementId, props } = config;
      if (this.mountedList.includes(name) || this.mountingCurrentApp !== name) return true;
      if (window[name] && typeof window[name] === 'function') {
        const appReference = window[name](elementId, props);
        appReference.mount();
        this.mountedList.push({ name, appReference });
        this.mountingCurrentApp = '';
        return true;
      }
      return false;
    };

    /**
     * loads or prefetch provided resource
     * @param {{url: String, prefetch: Boolean}} config - resource details
     * @returns {Promise}
     */
    loadResources = (config) => {
      const { url, prefetch = false } = config;

      return this.getJson(url)
        .then((response) => response.json())
        .then((json) => Promise.all(
          json.entrypoints.map((fileName) => (prefetch ? load.preFetch(url, fileName) : load.fetch(url, fileName))),
        )).catch((e) => console.log(e));
    };

    /**
     * getter for mountedList
     */
    getMountedMF = () => this.mountedList;

    /**
     * unmounts provided micro application
     * @param {{appReference: {unmount: Function}}} app
     */
    unmount = (app) => {
      const { appReference: { unmount } } = app;
      unmount && typeof unmount === 'function' && unmount();
    };

    /**
     * unmounts all mounted micro application
     */
    unmountAll = () => {
      this.mountedList.forEach(this.unmount);
      this.mountedList.length = 0;
    };

    /**
     * loads provided application and mounts it
     * @param {{name: String, elementId: String, props: Object}} config
     * @returns {Promise}
     */
    mount = (config) => new Promise((resolve, reject) => {
      this.mountingCurrentApp = config.name;
      this.unmountAll();
      const isLoaded = this.loadedList.reduce((result, appName) => result || config.name === appName, false);
      if (isLoaded) {
        this.mountMF(config) ? resolve(config.name) : reject(new Error(config.name));
      } else {
        this.loadResources(config)
          .then(() => {
            if (this.loadedList.indexOf(config.name) === -1) {
              this.loadedList.push(config.name);
            }
            this.mountMF(config) ? resolve(config.name) : reject(new Error(config.name));
          })
          .catch(() => {
            reject(new Error('Unable to open this screen'));
          });
      }
    });

    /**
     * Prefetch provided micro application if not already loaded
     * @param {{name: String, url: String, prefetch: Boolean}} config
     * @returns {Promise}
     */
    prefetchMF = (config) => new Promise((resolve, reject) => {
      const newConfig = {
        ...config,
        prefetch: true,
      };
      if (!this.loadedList.includes(newConfig.name)) {
        this.loadResources(newConfig).then(resolve).catch(reject);
      } else {
        resolve();
      }
    });
  }

  /**
   * initiates new instance of Microfrontend and returns
   * @returns {Microfrontend} instance
   */
  const createInstance = () => {
    instance = new Microfrontend();
    return instance;
  };

  /**
   * getter for instance
   */
  const getInstance = () => instance || createInstance();

  /**
   * clears Microfrontend instance
   */
  const clearInstance = () => { instance = undefined; };

  return {
    getInstance,
    clearInstance,
  };
})();
